import { useEffect, useRef, useState } from 'react';
import type { CardFaceDisplay } from '../lib/utils';
import { getTooltipImageVersion } from '../lib/utils';

// Display widths scale with the user's image-size preference.
// png is 745×1040 lossless — render at the same pixel width as large but sharper on HiDPI.
const SIZE_SINGLE: Record<string, number> = { normal: 250, large: 320, png: 320 };
const SIZE_DOUBLE: Record<string, number> = { normal: 200, large: 260, png: 260 };
const DOUBLE_GAP = 6;

function getSingleW(): number { return SIZE_SINGLE[getTooltipImageVersion()] ?? 250; }
function getDoubleW(): number { return SIZE_DOUBLE[getTooltipImageVersion()] ?? 200; }

/** Clamp a proposed tooltip position so the card image stays fully within the viewport. */
function clampPos(x: number, y: number, totalW: number): { x: number; y: number } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const estH = Math.round(totalW * (88 / 63) + 8);
  return {
    x: Math.max(8, Math.min(x, vw - totalW - 8)),
    y: Math.max(8, Math.min(y, vh - estH - 8)),
  };
}

interface CardTooltipState {
  faces: CardFaceDisplay[];
  x: number;
  y: number;
}

let _setState: ((s: CardTooltipState | null) => void) | null = null;
let _currentFaces: CardFaceDisplay[] | null = null;

/** Event types accepted by showCardTooltip */
export type TooltipTriggerEvent =
  | MouseEvent
  | React.MouseEvent
  | React.FocusEvent;

/** Show card image(s) near the cursor (mouse events) or element (focus events).
 *  Pass CardFaceDisplay[] from cardImageUris() for layout-aware rendering. */
export function showCardTooltip(faces: CardFaceDisplay | CardFaceDisplay[], e: TooltipTriggerEvent) {
  const faceArr = Array.isArray(faces) ? faces : [faces];
  let x: number;
  let y: number;

  const isMulti = faceArr.length > 1;
  const singleW = getSingleW();
  const doubleW = getDoubleW();
  const hasRotated = faceArr.some(f => f.rotate === 90);
  const rotatedH = Math.round(singleW * 1.4);
  const singleEffectiveW = hasRotated ? rotatedH : singleW;
  const totalW = isMulti ? doubleW * 2 + DOUBLE_GAP + 16 : singleEffectiveW + 16;

  if ('clientX' in e && typeof e.clientX === 'number') {
    x = e.clientX + 16;
    y = e.clientY + 8;
  } else {
    const rect = (e.currentTarget as Element).getBoundingClientRect();
    x = rect.right + 8;
    y = rect.top;
  }

  ({ x, y } = clampPos(x, y, totalW));

  _currentFaces = faceArr;
  _setState?.({ faces: faceArr, x, y });
}

/** Hide the tooltip */
export function hideCardTooltip() {
  _currentFaces = null;
  _setState?.(null);
}

export function CardTooltip() {
  const [state, setState] = useState<CardTooltipState | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    _setState = setState;
    return () => { _setState = null; };
  }, []);

  // Single stable mousemove listener — reads faces from module-level variable
  useEffect(() => {
    function onMouseMove(e: MouseEvent) {
      if (!_currentFaces) return;
      const isMulti = _currentFaces.length > 1;
      const singleW = getSingleW();
      const doubleW = getDoubleW();
      // Rotated images swap width/height visually — use card height as effective width
      const hasRotated = _currentFaces.some(f => f.rotate === 90);
      const rotatedH = Math.round(singleW * 1.4); // approx card height
      const singleEffectiveW = hasRotated ? rotatedH : singleW;
      const totalW = isMulti ? doubleW * 2 + DOUBLE_GAP + 16 : singleEffectiveW + 16;
      const { x, y } = clampPos(e.clientX + 16, e.clientY + 8, totalW);
      setState({ faces: _currentFaces, x, y });
    }

    document.addEventListener('mousemove', onMouseMove);
    return () => document.removeEventListener('mousemove', onMouseMove);
  }, []);

  if (!state) return null;

  const isMulti = state.faces.length > 1;
  const imgW = isMulti ? getDoubleW() : getSingleW();

  return (
    <div
      ref={ref}
      className="card-tooltip"
      style={{ left: state.x, top: state.y, display: 'flex', gap: isMulti ? DOUBLE_GAP : 0, alignItems: 'center' }}
      aria-hidden="true"
    >
      {state.faces.map((face, i) => {
        const rotStyle = face.rotate
          ? { transform: `rotate(${face.rotate}deg)`, transformOrigin: 'center center' }
          : undefined;
        // For 90° rotation we adjust the container height to match the rotated image's visual footprint
        const wrapStyle = face.rotate === 90
          ? { width: 350, height: imgW, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }
          : { flexShrink: 0 };
        return (
          <div key={i} style={wrapStyle}>
            <img
              src={face.src}
              alt={i === 0 ? 'Card front' : 'Card back'}
              style={{ width: imgW, ...rotStyle }}
              onError={(ev) => { (ev.target as HTMLImageElement).style.display = 'none'; }}
            />
          </div>
        );
      })}
    </div>
  );
}
